import { useState } from "react";
import { useLocation } from "wouter";
import { AuthShell, Field, SubmitButton } from "./AuthShell";
import { t } from "../../locale";
import { useAuth } from "../../stores/auth";

/**
 * Where the emailed recovery link lands.
 *
 * No captcha: the link itself is the proof. Supabase puts a recovery token in
 * the URL fragment and the client exchanges it for a session on load, so by the
 * time this renders the visitor is already authenticated and only needs to
 * choose a password. If they arrived without a session, the link was bad or
 * expired and there is nothing to set.
 */
export const ResetPassword = () => {
  const [, navigate] = useLocation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const session = useAuth((s) => s.session);
  const loading = useAuth((s) => s.loading);
  const updatePassword = useAuth((s) => s.updatePassword);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await updatePassword(password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t.auth.genericFailure);
    } finally {
      setBusy(false);
    }
  };

  if (!loading && !session) {
    return (
      <AuthShell title={t.auth.newPassword}>
        <p className="text-sm text-ink-dim">{t.auth.resetLinkInvalid}</p>
        <SubmitButton
          label={t.auth.forgotPassword}
          disabled={false}
          onClick={() => navigate("/auth/forgot")}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t.auth.newPassword} error={error}>
      <Field
        label={t.auth.password}
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
      />
      <SubmitButton
        label={t.auth.updatePassword}
        disabled={password.length === 0 || busy}
        onClick={submit}
      />
    </AuthShell>
  );
};
